import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import {
  checkCampaignNameAvailability,
  getCampaign,
  getCampaignAnalysisPreflight,
  getCampaignAnalysisResult,
  getCampaignAnalysisStatus,
  getCampaignCount,
  getCampaignCreatorsCount,
  getCampaignMetrics,
  getCampaignOverview,
  getCampaignPostsCount,
  getPendingCreatorsCount,
  getRecentPostsCount,
  listCampaignAnalyses,
  listCampaignCreators,
  listCampaignPosts,
  listCampaigns,
  listPendingCreators,
  listRecentPosts,
} from './generated/api';
import { CAMPAIGN_ANALYSIS_STATUS } from './generated/campaign-constants';
import type { CampaignAnalysisStatus } from './generated/campaign-type';
import {
  campaignQueryKeys,
  type CampaignCreatorCountRequest,
  type CampaignCreatorListRequest,
  type CampaignPostListRequest,
  type InfiniteCampaignListRequest,
} from './query-keys';
import { mapCampaignDetailResponseToCampaignDetail } from './util/campaign-detail-response';
import {
  mapCampaignCreatorListResponseToCampaignCreatorList,
  mapCampaignPostListResponseToCampaignPostList,
} from './util/campaign-list-responses';

export function isCampaignAnalysisTerminal(status: CampaignAnalysisStatus | null | undefined) {
  return (
    status === CAMPAIGN_ANALYSIS_STATUS.COMPLETED || status === CAMPAIGN_ANALYSIS_STATUS.FAILED
  );
}

export function campaignListInfiniteQueryOptions(
  workspaceId: string,
  request: InfiniteCampaignListRequest,
) {
  return infiniteQueryOptions({
    initialPageParam: 1,
    queryKey: campaignQueryKeys.infiniteLists(workspaceId, request),
    queryFn: async ({ pageParam, signal }) => {
      const response = await listCampaigns({
        path: { workspaceId },
        query: { ...request, page: pageParam },
        signal,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      const { page, limit, total } = lastPage.pagination;
      return page * limit >= total ? undefined : page + 1;
    },
    staleTime: 30_000,
  });
}

export function campaignSlotQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.slot(),
    queryFn: ({ signal }) => getCampaignCount({ path: { workspaceId }, signal }),
    staleTime: 0,
  });
}

export function campaignNameAvailabilityQueryOptions(workspaceId: string, name: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.nameAvailability(workspaceId, name),
    queryFn: ({ signal }) =>
      checkCampaignNameAvailability({ path: { workspaceId }, query: { name }, signal }),
    retry: false,
    staleTime: 30_000,
  });
}

export function campaignDetailQueryOptions(workspaceId: string, campaignId: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.detail(workspaceId, campaignId),
    queryFn: async ({ signal }) => {
      const response = await getCampaign({ path: { campaignId }, signal });
      return mapCampaignDetailResponseToCampaignDetail(response);
    },
    staleTime: 30_000,
  });
}

export function campaignOverviewQueryOptions(workspaceId: string, campaignId: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.overview(workspaceId, campaignId),
    queryFn: ({ signal }) => getCampaignOverview({ path: { campaignId }, signal }),
    staleTime: 30_000,
  });
}

export function campaignMetricsQueryOptions(workspaceId: string, campaignId: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.metrics(workspaceId, campaignId),
    queryFn: ({ signal }) => getCampaignMetrics({ path: { campaignId }, signal }),
  });
}

export function campaignAnalysisListQueryOptions(workspaceId: string, campaignId: string) {
  return infiniteQueryOptions({
    queryKey: campaignQueryKeys.analysisList(workspaceId, campaignId),
    queryFn: ({ pageParam, signal }) =>
      listCampaignAnalyses({
        path: { campaignId },
        query: pageParam == null ? undefined : { cursor: pageParam },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

export function campaignAnalysisPreflightQueryOptions(workspaceId: string, campaignId: string) {
  return queryOptions({
    queryKey: campaignQueryKeys.analysisPreflight(workspaceId, campaignId),
    queryFn: ({ signal }) => getCampaignAnalysisPreflight({ path: { campaignId }, signal }),
  });
}

export function campaignAnalysisResultQueryOptions(
  workspaceId: string,
  campaignId: string,
  runId: string,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.analysisResult(workspaceId, campaignId, runId),
    queryFn: ({ signal }) => getCampaignAnalysisResult({ path: { campaignId, runId }, signal }),
  });
}

export function campaignAnalysisStatusQueryOptions(
  workspaceId: string,
  campaignId: string,
  runId: string,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.analysisStatus(workspaceId, campaignId, runId),
    queryFn: ({ signal }) => getCampaignAnalysisStatus({ path: { campaignId, runId }, signal }),
  });
}

export function campaignCreatorsQueryOptions(
  workspaceId: string,
  campaignId: string,
  pending: boolean,
  request?: CampaignCreatorListRequest,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.creators(workspaceId, campaignId, pending, request),
    queryFn: async ({ signal }) => {
      const requestCreatorList = pending ? listPendingCreators : listCampaignCreators;
      const response = await requestCreatorList({
        path: { campaignId },
        query: request,
        signal,
      });
      return mapCampaignCreatorListResponseToCampaignCreatorList(response);
    },
    staleTime: 30_000,
  });
}

export function campaignCreatorCountQueryOptions(
  workspaceId: string,
  campaignId: string,
  pending: boolean,
  request?: CampaignCreatorCountRequest,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.creatorCount(workspaceId, campaignId, pending, request),
    queryFn: ({ signal }) => {
      const requestCreatorCount = pending ? getPendingCreatorsCount : getCampaignCreatorsCount;
      return requestCreatorCount({
        path: { campaignId },
        query: request,
        signal,
      }).then((response) => response.count);
    },
    staleTime: 30_000,
  });
}

export function campaignPostsQueryOptions(
  workspaceId: string,
  campaignId: string,
  recent: boolean,
  request?: CampaignPostListRequest,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.posts(workspaceId, campaignId, recent, request),
    queryFn: async ({ signal }) => {
      const requestPostList = recent ? listRecentPosts : listCampaignPosts;
      const response = await requestPostList({
        path: { campaignId },
        query: request,
        signal,
      });
      return mapCampaignPostListResponseToCampaignPostList(response);
    },
    staleTime: 30_000,
  });
}

export function campaignPostCountQueryOptions(
  workspaceId: string,
  campaignId: string,
  recent: boolean,
) {
  return queryOptions({
    queryKey: campaignQueryKeys.postCount(workspaceId, campaignId, recent),
    queryFn: ({ signal }) =>
      (recent ? getRecentPostsCount : getCampaignPostsCount)({
        path: { campaignId },
        signal,
      }).then((response) => response.count),
    staleTime: 30_000,
  });
}
